<template>
    <transition name="app-modal-fade">
        <div v-if="value" class="app-modal-backdrop"
            @mousedown.self="onBackdropMouseDown"
            @mouseup.self="onBackdropMouseUp">
            <div class="app-modal" :class="{ 'app-modal--lg': size === 'lg' }" role="dialog" aria-modal="true">

                <header class="app-modal__header">
                    <slot name="header">
                        <h3 class="app-modal__title">{{ title }}</h3>
                    </slot>
                    <button type="button" class="app-modal__close" @click="close" aria-label="Cerrar modal">
                        &times;
                    </button>
                </header>

                <main class="app-modal__body">
                    <slot></slot>
                </main>

                <footer class="app-modal__footer" v-if="$slots.footer">
                    <slot name="footer"></slot>
                </footer>

            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'AppModal',
    props: {
        // Vue 2 usa 'value' por defecto para v-model
        value: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        // 'md' (500px) o 'lg' (900px)
        size: {
            type: String,
            default: 'md'
        }
    },
    data() {
        return {
            // Rastrea si el mousedown ocurrió en el backdrop
            mouseDownOnBackdrop: false
        };
    },
    watch: {
        // Escuchamos si el modal se abre o cierra para manejar el scroll y eventos
        value(isOpen) {
            if (isOpen) {
                document.body.style.overflow = 'hidden';
                document.addEventListener('keydown', this.handleEsc);
            } else {
                document.body.style.overflow = '';
                document.removeEventListener('keydown', this.handleEsc);
            }
        }
    },
    methods: {
        onBackdropMouseDown() {
            this.mouseDownOnBackdrop = true;
        },
        onBackdropMouseUp() {
            // Solo cierra si tanto mousedown como mouseup ocurrieron en el backdrop
            if (this.mouseDownOnBackdrop) {
                this.close();
            }
            this.mouseDownOnBackdrop = false;
        },
        close() {
            // Emitimos 'input' en false para actualizar el v-model del componente padre
            this.$emit('input', false);
        },
        handleEsc(e) {
            if (e.key === 'Escape') {
                this.close();
            }
        }
    },
    // Limpieza vital por si el componente se destruye abruptamente
    destroyed() {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleEsc);
    }
}
</script>

<style scoped>
/* Variables locales para facilitar futuras modificaciones */
.app-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
    /* Z-index alto para sobreponerse a todo */
}

.app-modal {
    background: #ffffff;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    margin: 1rem;
}

.app-modal--lg {
    max-width: 900px;
}

.app-modal__header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--app-border-color, #e5e7eb);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.app-modal__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #374151;
}

.app-modal__close {
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    line-height: 0;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    transition: color 0.2s;
}

.app-modal__close:focus {
    outline: none;
    box-shadow: none;
}

.app-modal__close:hover {
    color: #111827;
}

.app-modal__body {
    padding: 1.5rem;
    overflow-y: auto;
    /* Permite scroll interno si el contenido es muy largo */
}

.app-modal__footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--app-border-color, #e5e7eb);
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

/* Clases de transición de Vue */
.app-modal-fade-enter-active,
.app-modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.app-modal-fade-enter,
.app-modal-fade-leave-to {
    opacity: 0;
}

.app-modal-fade-enter-active .app-modal {
    animation: modal-pop 0.2s ease-out;
}

@keyframes modal-pop {
    0% {
        transform: scale(0.95);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>